/**
 * `useRemoveFavorite` — unpin a wiki node from the caller's Favorites.
 *
 * Wraps `DELETE /api/wiki/favorites/:nodeId`. Optimistically removes the
 * matching favorite row from the cached `wikiTree.favorites` array;
 * rollbacks on error.
 *
 * Settle invalidation: `['wiki', 'tree']` so cross-tab realtime stays
 * consistent.
 *
 * `apiClient` auto-injects `Idempotency-Key`; double-clicks on the
 * remove button hit the cached BE response (no orphan retry).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '@/api/client'
import type { WikiTreeResponse } from '@/api/types-bridge'
import { useToast } from '@/components/ToastProvider'

import { WIKI_TREE_QUERY_KEY } from './query-keys'

export interface UseRemoveFavoriteInput {
  nodeId: string
}

interface RemoveFavoriteContext {
  previous: WikiTreeResponse | undefined
}

export function useRemoveFavorite() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation('wiki')

  return useMutation({
    mutationFn: async (input: UseRemoveFavoriteInput): Promise<void> => {
      await apiClient<unknown>(`/api/wiki/favorites/${encodeURIComponent(input.nodeId)}`, {
        method: 'DELETE',
      })
    },

    onMutate: async (input): Promise<RemoveFavoriteContext> => {
      await queryClient.cancelQueries({ queryKey: WIKI_TREE_QUERY_KEY })
      const previous = queryClient.getQueryData<WikiTreeResponse>(WIKI_TREE_QUERY_KEY)
      if (previous) {
        queryClient.setQueryData<WikiTreeResponse>(WIKI_TREE_QUERY_KEY, {
          ...previous,
          favorites: previous.favorites.filter((f) => f.id !== input.nodeId),
        })
      }
      return { previous }
    },

    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(WIKI_TREE_QUERY_KEY, context.previous)
      }
      toast.error(t('favorites.removeFailedToast'))
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: WIKI_TREE_QUERY_KEY })
    },
  })
}
